/**
 * Covenants API functions
 *
 * Covers:
 *   - CovenantViewSet  (/api/covenants/covenants/)
 *   - CharacterCovenantRoleViewSet (/api/covenants/character-roles/)
 *     including engage/disengage custom actions (Phase 7)
 *
 * Uses apiFetch from @/evennia_replacements/api.
 */

import { apiFetch, withQuery } from '@/evennia_replacements/api';
import { parseDispatchBody, readErrorDetail } from '@/lib/errors';
import { performRitual } from '@/rituals/api';
import type { components } from '@/generated/api';

// ---------------------------------------------------------------------------
// Generated type aliases
// ---------------------------------------------------------------------------

export type Covenant = components['schemas']['Covenant'];
export type CharacterCovenantRole = components['schemas']['CharacterCovenantRole'];
export type CovenantRole = components['schemas']['CovenantRole'];
export type PaginatedCovenantList = components['schemas']['PaginatedCovenantList'];
export type PaginatedCharacterCovenantRoleList =
  components['schemas']['PaginatedCharacterCovenantRoleList'];
export type CovenantRank = components['schemas']['CovenantRank'];
export type CovenantRankNested = components['schemas']['CovenantRankNested'];
export type PaginatedCovenantRankList = components['schemas']['PaginatedCovenantRankList'];

/** Typed cast of the generated `{[key: string]: unknown}` viewer_capabilities block. */
export interface ViewerCapabilities {
  can_invite: boolean;
  can_kick: boolean;
  can_manage_ranks: boolean;
  /** May post an open ask for a GM to run a story for this covenant (#2119). */
  can_request_gm: boolean;
}

// ---------------------------------------------------------------------------
// Hand-written schema extensions (until OpenAPI regen at integration; see #518)
// ---------------------------------------------------------------------------
//
// The generated api.d.ts is a single shared file derived from ALL serializers
// across the app; regenerating it on this branch would collide with combat
// serializer changes on another branch. So these new serializer fields are
// declared by hand here, scoped to the covenants module, until the schema is
// regenerated at integration.

/** Battle-state fields added to CovenantSerializer in C1. */
export interface CovenantBattleFields {
  is_dormant: boolean;
  battle_binding: string | null;
  battle_binding_display: string;
}

/** Covenant detail payload including the C1 battle-state fields. */
export type CovenantWithBattleState = Covenant & CovenantBattleFields;

/** CovenantRole payload including the parent_role FK added in C4. */
export interface CovenantRoleWithParent extends CovenantRole {
  parent_role: number | null;
}

// ---------------------------------------------------------------------------
// Hand-written types for the powers / stand-down endpoints
// ---------------------------------------------------------------------------

/**
 * One authored CovenantRite row as returned by the powers endpoint, with the
 * per-covenant gate flags (level_met / members_present_met) the serializer adds.
 */
export interface CovenantRiteRow {
  id: number;
  ritual: number;
  covenant_type: string;
  covenant_type_display: string;
  min_covenant_level: number;
  min_members_present: number;
  granted_condition: number | null;
  base_severity: number;
  severity_per_extra_participant: number;
  // Nullable on the backend model (null=True) → serialized as number | null.
  max_severity: number | null;
  duration_rounds: number | null;
  level_met: boolean;
  members_present_met: boolean;
}

/**
 * One active membership's current passive role power, as returned by the powers
 * endpoint. Members without a woven role-thread have null capability fields.
 */
export interface RolePower {
  membership_id: number;
  character_sheet: number;
  covenant_role_id: number;
  covenant_role_name: string;
  resonance_name: string | null;
  capability_name: string | null;
  narrative_snippet: string | null;
  engaged: boolean;
}

/** Combined payload from GET /api/covenants/covenants/{id}/powers/. */
export interface CovenantPowers {
  rites: CovenantRiteRow[];
  role_powers: RolePower[];
}

/** Response from POST /api/covenants/covenants/{id}/stand_down/. */
export interface StandDownResult {
  id: number;
  is_dormant: boolean;
  battle_binding: string;
}

// ---------------------------------------------------------------------------
// URL constants
// ---------------------------------------------------------------------------

const COVENANTS_URL = '/api/covenants/covenants';
const CHARACTER_ROLES_URL = '/api/covenants/character-roles';
const ROLES_URL = '/api/covenants/roles';
const RANKS_URL = '/api/covenants/ranks';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function jsonHeaders(): HeadersInit {
  return { 'Content-Type': 'application/json' };
}

// ---------------------------------------------------------------------------
// Covenant reads
// ---------------------------------------------------------------------------

export async function getCovenants(): Promise<PaginatedCovenantList> {
  const res = await apiFetch(`${COVENANTS_URL}/`);
  if (!res.ok) throw new Error('Failed to load covenants');
  return res.json() as Promise<PaginatedCovenantList>;
}

export async function getCovenant(id: number): Promise<CovenantWithBattleState> {
  const res = await apiFetch(`${COVENANTS_URL}/${id}/`);
  if (!res.ok) throw new Error(`Failed to load covenant ${id}`);
  return res.json() as Promise<CovenantWithBattleState>;
}

// ---------------------------------------------------------------------------
// CharacterCovenantRole reads
// ---------------------------------------------------------------------------

/**
 * GET /api/covenants/character-roles/?covenant={covenantId}
 * Returns all membership rows for the given covenant.
 */
export async function getCharacterRolesForCovenant(
  covenantId: number
): Promise<PaginatedCharacterCovenantRoleList> {
  const res = await apiFetch(`${CHARACTER_ROLES_URL}/?covenant=${covenantId}`);
  if (!res.ok) throw new Error(`Failed to load members for covenant ${covenantId}`);
  return res.json() as Promise<PaginatedCharacterCovenantRoleList>;
}

// ---------------------------------------------------------------------------
// Engage / disengage mutations
// ---------------------------------------------------------------------------

/**
 * POST /api/covenants/character-roles/{id}/engage/
 * Engage a covenant role. Returns the updated CharacterCovenantRole row.
 */
export async function engageMembership(id: number): Promise<CharacterCovenantRole> {
  const res = await apiFetch(`${CHARACTER_ROLES_URL}/${id}/engage/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({}),
  });
  if (!res.ok) await readErrorDetail(res, 'Failed to engage covenant role');
  return res.json() as Promise<CharacterCovenantRole>;
}

/**
 * POST /api/covenants/character-roles/{id}/disengage/
 * Disengage a covenant role. Returns the updated CharacterCovenantRole row.
 */
export async function disengageMembership(id: number): Promise<CharacterCovenantRole> {
  const res = await apiFetch(`${CHARACTER_ROLES_URL}/${id}/disengage/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({}),
  });
  if (!res.ok) await readErrorDetail(res, 'Failed to disengage covenant role');
  return res.json() as Promise<CharacterCovenantRole>;
}

// ---------------------------------------------------------------------------
// Leave / kick mutations (exit lifecycle, #519)
// ---------------------------------------------------------------------------

/**
 * POST /api/covenants/character-roles/{id}/leave/
 * Voluntary self-leave. Returns the updated CharacterCovenantRole row.
 */
export async function leaveMembership(id: number): Promise<CharacterCovenantRole> {
  const res = await apiFetch(`${CHARACTER_ROLES_URL}/${id}/leave/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({}),
  });
  if (!res.ok) await readErrorDetail(res, 'Failed to leave covenant');
  return res.json() as Promise<CharacterCovenantRole>;
}

/**
 * POST /api/covenants/character-roles/{id}/kick/
 * A leader removes a non-leader member. Returns the updated target row.
 */
export async function kickMember(id: number): Promise<CharacterCovenantRole> {
  const res = await apiFetch(`${CHARACTER_ROLES_URL}/${id}/kick/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({}),
  });
  if (!res.ok) await readErrorDetail(res, 'Failed to remove member');
  return res.json() as Promise<CharacterCovenantRole>;
}

// ---------------------------------------------------------------------------
// Powers read
// ---------------------------------------------------------------------------

/**
 * GET /api/covenants/covenants/{id}/powers/
 * Returns the covenant's authored rites (with gate flags) and per-member
 * passive role powers in one payload.
 */
export async function getCovenantPowers(covenantId: number): Promise<CovenantPowers> {
  const res = await apiFetch(`${COVENANTS_URL}/${covenantId}/powers/`);
  if (!res.ok) throw new Error(`Failed to load powers for covenant ${covenantId}`);
  return res.json() as Promise<CovenantPowers>;
}

// ---------------------------------------------------------------------------
// Stand-down mutation (battle covenants)
// ---------------------------------------------------------------------------

/**
 * POST /api/covenants/covenants/{id}/stand_down/
 * Stand a raised battle covenant down to dormant. Returns its new battle state.
 */
export async function standDownCovenant(covenantId: number): Promise<StandDownResult> {
  const res = await apiFetch(`${COVENANTS_URL}/${covenantId}/stand_down/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({}),
  });
  if (!res.ok) await readErrorDetail(res, 'Failed to stand down covenant');
  return res.json() as Promise<StandDownResult>;
}

// ---------------------------------------------------------------------------
// Promotion mutation
// ---------------------------------------------------------------------------

/**
 * POST /api/covenants/character-roles/{id}/promote/
 * Promote a membership into one of its current role's sub-roles. Returns the
 * new CharacterCovenantRole row.
 */
export async function promoteMembership(
  membershipId: number,
  targetSubroleId: number
): Promise<CharacterCovenantRole> {
  const res = await apiFetch(`${CHARACTER_ROLES_URL}/${membershipId}/promote/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ target_subrole: targetSubroleId }),
  });
  if (!res.ok) await readErrorDetail(res, 'Failed to promote covenant role');
  return res.json() as Promise<CharacterCovenantRole>;
}

// ---------------------------------------------------------------------------
// CovenantRole lookup (unpaginated)
// ---------------------------------------------------------------------------

/**
 * GET /api/covenants/roles/?covenant_type=&parent_role=
 * Returns the (unpaginated) CovenantRole lookup list. Pass `parent_role` to
 * fetch a role's sub-roles for the promotion picker.
 */
export async function getCovenantRoles(params: {
  covenant_type?: string;
  parent_role?: number;
}): Promise<CovenantRoleWithParent[]> {
  const search = new URLSearchParams();
  if (params.covenant_type != null) search.set('covenant_type', params.covenant_type);
  if (params.parent_role != null) search.set('parent_role', String(params.parent_role));
  const query = search.toString();
  const res = await apiFetch(withQuery(`${ROLES_URL}/`, query));
  if (!res.ok) throw new Error('Failed to load covenant roles');
  return res.json() as Promise<CovenantRoleWithParent[]>;
}

// ---------------------------------------------------------------------------
// Rank reads
// ---------------------------------------------------------------------------

/**
 * GET /api/covenants/ranks/?covenant={covenantId}
 * Returns all ranks for the given covenant, ordered by tier.
 */
export async function getCovenantRanksForCovenant(
  covenantId: number
): Promise<PaginatedCovenantRankList> {
  const res = await apiFetch(`${RANKS_URL}/?covenant=${covenantId}`);
  if (!res.ok) throw new Error(`Failed to load ranks for covenant ${covenantId}`);
  return res.json() as Promise<PaginatedCovenantRankList>;
}

// ---------------------------------------------------------------------------
// Rank mutations
// ---------------------------------------------------------------------------

/**
 * POST /api/covenants/ranks/
 * Create a new rank for the given covenant.
 */
export async function createCovenantRank(data: {
  covenant: number;
  name: string;
  tier: number;
  description?: string;
  can_invite?: boolean;
  can_kick?: boolean;
  can_manage_ranks?: boolean;
}): Promise<CovenantRank> {
  const res = await apiFetch(`${RANKS_URL}/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) await readErrorDetail(res, 'Failed to create rank');
  return res.json() as Promise<CovenantRank>;
}

/**
 * PATCH /api/covenants/ranks/{id}/
 * Partial-update a rank (rename, toggle capability flags).
 */
export async function updateCovenantRank(
  id: number,
  data: Partial<{
    name: string;
    description: string;
    can_invite: boolean;
    can_kick: boolean;
    can_manage_ranks: boolean;
  }>
): Promise<CovenantRank> {
  const res = await apiFetch(`${RANKS_URL}/${id}/`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) await readErrorDetail(res, 'Failed to update rank');
  return res.json() as Promise<CovenantRank>;
}

/**
 * DELETE /api/covenants/ranks/{id}/
 * Delete a rank, reassigning its members to `reassignTo`.
 */
export async function deleteCovenantRank(id: number, reassignTo: number): Promise<void> {
  const res = await apiFetch(`${RANKS_URL}/${id}/`, {
    method: 'DELETE',
    headers: jsonHeaders(),
    body: JSON.stringify({ reassign_to: reassignTo }),
  });
  if (!res.ok) await readErrorDetail(res, 'Failed to delete rank');
}

/**
 * POST /api/covenants/ranks/{rankId}/assign-member/
 * Assign a member to this rank.
 */
export async function assignMemberToRank(
  rankId: number,
  membershipId: number
): Promise<CharacterCovenantRole> {
  const res = await apiFetch(`${RANKS_URL}/${rankId}/assign-member/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ membership: membershipId }),
  });
  if (!res.ok) await readErrorDetail(res, 'Failed to assign member to rank');
  return res.json() as Promise<CharacterCovenantRole>;
}

/**
 * POST /api/covenants/ranks/reorder/
 * Reorder all ranks for a covenant.
 */
export async function reorderRanks(
  covenantId: number,
  orderedRankIds: number[]
): Promise<CovenantRank[]> {
  const res = await apiFetch(`${RANKS_URL}/reorder/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ covenant: covenantId, ordered_rank_ids: orderedRankIds }),
  });
  if (!res.ok) await readErrorDetail(res, 'Failed to reorder ranks');
  return res.json() as Promise<CovenantRank[]>;
}

// ---------------------------------------------------------------------------
// GroupStoryRequest (#2119) — player->GM recruitment loop
// ---------------------------------------------------------------------------
//
// Hand-authored (not generated) — same reasoning as the block above: this is
// a brand-new model/serializer/viewset, and regenerating the shared schema
// file on this branch risks colliding with unrelated in-flight branches.
// Mutation goes exclusively through the generic action-dispatch endpoint
// (POST /api/actions/characters/{id}/dispatch/, see @/combat/api's
// postDispatchAction) — never a bespoke @action here (Decision 8, #2119).

export type GroupStoryRequestStatus = 'pending' | 'accepted' | 'withdrawn';

/** One GroupStoryRequest row, from GET /api/group-story-requests/. */
export interface GroupStoryRequest {
  id: number;
  covenant: number;
  requested_by_account: number;
  message: string;
  status: GroupStoryRequestStatus;
  claimed_by: number | null;
  created_story: number | null;
  created_at: string;
  responded_at: string | null;
  updated_at: string;
}

export interface PaginatedGroupStoryRequestList {
  count: number;
  next: string | null;
  previous: string | null;
  results: GroupStoryRequest[];
}

const GROUP_STORY_REQUESTS_URL = '/api/group-story-requests';

/**
 * GET /api/group-story-requests/?covenant={covenantId}&status=pending
 * Returns the covenant's current open (PENDING) request, if any.
 */
export async function getPendingGroupStoryRequestForCovenant(
  covenantId: number
): Promise<GroupStoryRequest | null> {
  const res = await apiFetch(`${GROUP_STORY_REQUESTS_URL}/?covenant=${covenantId}&status=pending`);
  if (!res.ok) throw new Error(`Failed to load GM requests for covenant ${covenantId}`);
  const data = (await res.json()) as PaginatedGroupStoryRequestList;
  return data.results[0] ?? null;
}

/**
 * Dispatch RequestGMForCovenantAction as the actor's own character
 * (actorCharacterId is the ObjectDB pk — doubles as the character_sheet pk,
 * since CharacterSheet.character is a primary_key=True OneToOneField).
 * Mirrors the generic-dispatch pattern in @/game/api/roomEditor.ts.
 *
 * `DispatchActionView` resolves HTTP 200 even for a business-rule rejection
 * (e.g. an already-open request) — `success === false` is the wire signal
 * that distinguishes an honest failure from a real success, and `res.ok`
 * alone is NOT enough (#3155; same fix shape as the treasury calls below).
 */
export async function requestGMForCovenant(
  actorCharacterId: number,
  covenantId: number,
  message: string
): Promise<string> {
  const res = await apiFetch(`/api/actions/characters/${actorCharacterId}/dispatch/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      ref: { backend: 'registry', registry_key: 'request_gm_for_covenant' },
      kwargs: { covenant_id: covenantId, message },
    }),
  });
  const { success, message: detail } = await parseDispatchBody(res);
  if (!res.ok || success === false) throw new Error(detail ?? 'Failed to post the GM request.');
  return detail ?? 'GM request posted.';
}

/**
 * Dispatch WithdrawGroupStoryRequestAction as the actor's own character.
 * See `requestGMForCovenant`'s doc comment for the `success === false` wire
 * signal this checks (#3155).
 */
export async function withdrawGroupStoryRequest(
  actorCharacterId: number,
  requestId: number
): Promise<string> {
  const res = await apiFetch(`/api/actions/characters/${actorCharacterId}/dispatch/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      ref: { backend: 'registry', registry_key: 'withdraw_group_story_request' },
      kwargs: { request_id: requestId },
    }),
  });
  const { success, message: detail } = await parseDispatchBody(res);
  if (!res.ok || success === false) throw new Error(detail ?? 'Failed to withdraw the GM request.');
  return detail ?? 'GM request withdrawn.';
}

// ---------------------------------------------------------------------------
// Covenant treasury (#2992) — dispatch through the generic action-dispatch
// endpoint, same seam as the GroupStoryRequest actions above. The action
// resolves the actor's OWN active membership in ``covenant_id`` server-side
// (actions/definitions/covenants.py::_resolve_actor_membership) — the wire
// never carries a membership id, so there is no way to act on someone else's
// membership from here.
// ---------------------------------------------------------------------------

/**
 * Dispatch DepositCovenantFundsAction as the actor's own character.
 * Moves ``amount`` coppers from the actor's purse into the covenant treasury.
 * See `requestGMForCovenant`'s doc comment for the `success === false` wire
 * signal this checks (originally #2992; hoisted onto the shared
 * `parseDispatchBody` in #3155).
 */
export async function depositCovenantFunds(
  actorCharacterId: number,
  covenantId: number,
  amount: number
): Promise<string> {
  const res = await apiFetch(`/api/actions/characters/${actorCharacterId}/dispatch/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      ref: { backend: 'registry', registry_key: 'deposit_covenant_funds' },
      kwargs: { covenant_id: covenantId, amount },
    }),
  });
  const { success, message } = await parseDispatchBody(res);
  if (!res.ok || success === false) throw new Error(message ?? 'Failed to deposit covenant funds.');
  return message ?? 'Funds deposited.';
}

/**
 * Dispatch WithdrawCovenantFundsAction as the actor's own character.
 * Moves ``amount`` coppers from the covenant treasury into the actor's purse.
 * Rank-gated server-side (``treasury.spend_rank_max``) — a rejection (HTTP 200,
 * ``success: false``) surfaces the curated ``user_message`` via the thrown Error.
 */
export async function withdrawCovenantFunds(
  actorCharacterId: number,
  covenantId: number,
  amount: number
): Promise<string> {
  const res = await apiFetch(`/api/actions/characters/${actorCharacterId}/dispatch/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      ref: { backend: 'registry', registry_key: 'withdraw_covenant_funds' },
      kwargs: { covenant_id: covenantId, amount },
    }),
  });
  const { success, message } = await parseDispatchBody(res);
  if (!res.ok || success === false)
    throw new Error(message ?? 'Failed to withdraw covenant funds.');
  return message ?? 'Funds withdrawn.';
}

// ---------------------------------------------------------------------------
// Vow focus picks (#4929): read the holder's catalog, choose through dispatch.
// ---------------------------------------------------------------------------

export type VowPickCatalog = components['schemas']['VowPickCatalog'];
export type VowPickLevel = components['schemas']['VowPickLevel'];
export type VowPickEntry = components['schemas']['VowPickEntry'];

/**
 * GET /api/covenants/character-roles/vow-picks/?character_sheet={sheetId}
 * Every pick each primary vow offers, by level. Self-only server-side.
 */
export async function getVowPicks(sheetId: number): Promise<VowPickCatalog[]> {
  const query = new URLSearchParams({ character_sheet: String(sheetId) });
  const res = await apiFetch(withQuery(`${CHARACTER_ROLES_URL}/vow-picks/`, query));
  if (!res.ok) return readErrorDetail(res, 'Failed to load vow picks.');
  return res.json() as Promise<VowPickCatalog[]>;
}

/**
 * Dispatch ChooseVowFocusPickAction as the character itself. The action looks the
 * pick up among the character's own vows; a refusal (HTTP 200, `success: false`)
 * throws with the rule's own message.
 */
export async function chooseVowPick(
  characterId: number,
  covenantId: number,
  pickId: number
): Promise<string> {
  const res = await apiFetch(`/api/actions/characters/${characterId}/dispatch/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      ref: { backend: 'registry', registry_key: 'choose_vow_focus_pick' },
      kwargs: { covenant_id: covenantId, pick_id: pickId },
    }),
  });
  const { success, message } = await parseDispatchBody(res);
  if (!res.ok || success === false) throw new Error(message ?? 'Failed to choose the pick.');
  return message ?? 'Pick chosen.';
}

// ---------------------------------------------------------------------------
// The Rite of Re-swearing (#4940): read the rite's costs, perform it.
// ---------------------------------------------------------------------------

export type ReswearOptions = components['schemas']['ReswearOptions'];

/**
 * GET /api/covenants/character-roles/reswear-options/?character_sheet={sheetId}
 * The rite, its AP, anima and component costs, and the carried items that pay it.
 */
export async function getReswearOptions(sheetId: number): Promise<ReswearOptions> {
  const query = new URLSearchParams({ character_sheet: String(sheetId) });
  const res = await apiFetch(withQuery(`${CHARACTER_ROLES_URL}/reswear-options/`, query));
  if (!res.ok) return readErrorDetail(res, 'Failed to load the Rite of Re-swearing.');
  return res.json() as Promise<ReswearOptions>;
}

export interface ReswearRequest {
  sheetId: number;
  ritualId: number;
  covenantId: number;
  oldPickId: number;
  newPickId: number;
  componentIds: number[];
}

/**
 * Perform the rite through POST /api/magic/rituals/perform/ (PerformRitualAction).
 * A refusal (HTTP 400) throws with the rule's own message; success returns the line
 * telnet prints.
 */
export async function performReswear(request: ReswearRequest): Promise<string> {
  const response = await performRitual({
    character_sheet_id: request.sheetId,
    ritual_id: request.ritualId,
    kwargs: {
      old_pick_id: request.oldPickId,
      new_pick_id: request.newPickId,
      covenant_id: request.covenantId,
    },
    components: request.componentIds,
  });
  return response.message ?? 'The rite is done.';
}
